'use client';

import { MockUser } from '@/types';
import { useLanguage } from '@/context/LanguageContext';

interface AttendeeAvatarsProps {
  attendees: { id: number }[];
  users: MockUser[];
  maxVisible?: number;
  size?: 'sm' | 'md';
  emptyText?: string;
  countLabel?: string;
}

export function AttendeeAvatars({
  attendees,
  users,
  maxVisible = 7,
  size = 'sm',
  emptyText,
  countLabel,
}: AttendeeAvatarsProps) {
  const { t } = useLanguage();
  const matchedAttendees = users.filter(user => attendees.some(a => a.id === user.id));
  const totalCount = matchedAttendees.length;

  if (totalCount === 0) {
    return <span className="text-sm text-foreground-muted">{emptyText ?? t.attendees.zeroAttending}</span>;
  }

  const visibleAttendees = matchedAttendees.slice(0, maxVisible);
  const remainingCount = totalCount - maxVisible;

  const avatarSize = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const fontSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center -space-x-2">
        {visibleAttendees.map((user, index) => (
          <div
            key={user.id}
            className={`${avatarSize} rounded-full border-2 border-white overflow-hidden flex-shrink-0`}
            style={{ zIndex: visibleAttendees.length - index }}
            title={user.displayName}
          >
            {user.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt={user.displayName} 
                className="w-full h-full object-contain"
              />
            ) : (
              <div
                className={`w-full h-full flex items-center justify-center text-white font-bold ${fontSize}`}
                style={{ backgroundColor: user.avatarColor }}
              >
                {user.initials}
              </div>
            )}
          </div>
        ))}
        {remainingCount > 0 && (
          <div
            className={`${avatarSize} rounded-full border-2 border-white bg-gray-200 flex items-center justify-center flex-shrink-0 ${fontSize} font-medium text-gray-700`}
            style={{ zIndex: 0 }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
      <span className="text-sm text-foreground-muted whitespace-nowrap">
        {totalCount} {countLabel ?? t.attendees.attending}
      </span>
    </div>
  );
}


